/**
 * AIFieldBadge
 *
 * Tiny inline indicator shown next to a load form input when the value was
 * auto-filled by Gemini Vision. Hover for source-doc + timestamp. Click X to
 * dismiss (calls DELETE /api/shipments/{id}/ai-autofill/{field}).
 *
 * Renders nothing when no `meta` is provided.
 */
import React, { useState } from 'react';
import { Sparkles, AlertTriangle, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const formatStamp = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

export const AIFieldBadge = ({ meta, shipmentId, field, api, onDismissed }) => {
  const [busy, setBusy] = useState(false);
  if (!meta) return null;

  const lowConf =
    meta.confidence && String(meta.confidence).toLowerCase() !== 'high';

  const dismiss = async (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (busy || !api || !shipmentId || !field) return;
    setBusy(true);
    try {
      await api.delete(`/shipments/${shipmentId}/ai-autofill/${field}`);
      onDismissed?.(field);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to dismiss');
    } finally {
      setBusy(false);
    }
  };

  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium border ${
        lowConf
          ? 'bg-amber-900/30 text-amber-300 border-amber-700/50'
          : 'bg-violet-900/30 text-violet-300 border-violet-700/50'
      }`}
      title={`Auto-filled from ${meta.filename || 'document'}${
        meta.confidence ? ` · confidence: ${meta.confidence}` : ''
      }${meta.at ? ` · ${formatStamp(meta.at)}` : ''}`}
      data-testid={`ai-badge-${field}`}
    >
      {lowConf ? (
        <AlertTriangle className="h-2.5 w-2.5" />
      ) : (
        <Sparkles className="h-2.5 w-2.5" />
      )}
      <span>{lowConf ? 'Review' : 'AI'}</span>
      <button
        type="button"
        onClick={dismiss}
        disabled={busy}
        className="ml-0.5 hover:opacity-80 disabled:opacity-50"
        aria-label={`Dismiss AI badge on ${field}`}
        data-testid={`ai-badge-dismiss-${field}`}
      >
        {busy ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <X className="h-2.5 w-2.5" />}
      </button>
    </span>
  );
};

export default AIFieldBadge;
